import {
  Box,
  Checkbox,
  CheckboxGroup,
  Divider,
  FormControl,
  FormLabel,
  Grid,
  HStack,
  Input,
  InputGroup,
  InputLeftAddon,
  InputRightAddon,
  PinInput,
  PinInputField,
  Select,
  Skeleton,
  Stack,
  Text,
  Textarea,
  useColorModeValue,
} from '@chakra-ui/react';
import IconTooltip from 'components/IconTooltip';
import i18n from 'i18next';
import { forEach, get, isNil, noop } from 'lodash';
import { FC, ReactElement, ReactNode } from 'react';
import { Control, Controller, FieldPath, FieldValues } from 'react-hook-form';
import { reach } from 'yup';

import { SingleDatepicker } from '../components/DatePicker';
import { DropzoneInput } from '../components/Dropzone';
import { DropdownOption } from '../components/Table/Filter';
import { getArrayForGroupedDropdown } from './general';

export const isRequired = ({ field = '', schema = null } = {}): boolean =>
  schema ? get(reach(schema, field), `exclusiveTests.required`, false) : false;

export const createDropdownOptionsNumbers = (records: Array<any>) => {
  let returnArray = [{ label: i18n.t('common:choose'), value: '' }];

  forEach(records, (value) => {
    returnArray.push({
      label: value,
      value: value,
    });
  });
  return returnArray;
};

export const createDropdownOptions = (records: Array<any>) => {
  let returnArray = [{ label: i18n.t('common:choose'), value: '' }];

  forEach(records, (value) => {
    returnArray.push({
      label: i18n.t('common:form.' + value.replace(/\s/g, '-'), value) as string,
      value: value,
    });
  });
  return returnArray;
};

export const RequiredAsterisk: FC<{}> = () => (
  <span>
    <sup>*</sup>
  </span>
);

export const simpleCheckBox = (props: {
  name: string;
  label?: string;
  // eslint-disable-next-line no-undef
  description?: string | JSX.Element | undefined;
  showAsTooltip?: boolean;
  register: any;
  errors: any;
  defaultChecked?: boolean;
  schema?: any;
  disabled?: boolean;
  size?: string;
}) => {
  const { name, label, description, register, errors, defaultChecked = false, showAsTooltip = false, disabled = false, size = 'lg', schema } = props;
  return (
    <FormControl id={name}>
      <Stack textAlign="left">
        <HStack alignItems={'flex-start'}>
          <Checkbox
            {...register(name)}
            defaultChecked={defaultChecked}
            borderColor={!isNil(errors[name]) && 'error'}
            size={size}
            p={2}
            pl={0}
            pb={0}
            disabled={disabled}
          />
          <Stack alignSelf={'center'}>
            {/* @ts-expect-error */}
            <FormLabel m={0} paddingBlockStart={1} htmlFor={name} textAlign="baseline">
              <Text color={disabled ? 'disabled' : 'inherit'}>
                {Boolean(label) && label}
                {isRequired({ field: name, schema }) && <RequiredAsterisk />}
              </Text>
              {Boolean(description) && !showAsTooltip && (
                <Text color={disabled ? 'disabled' : 'inherit'} fontSize="xs">
                  {description}
                </Text>
              )}
              {Boolean(description) && !disabled && showAsTooltip && <IconTooltip text={description}></IconTooltip>}
            </FormLabel>
          </Stack>
        </HStack>
        <Box pl={10}>
          <Text fontSize="sm" color={'error'} data-test-id={name + '_error'}>
            {get(errors, name, {})?.message}
          </Text>
        </Box>
      </Stack>
    </FormControl>
  );
};

export const groupedCheckBoxes = (props: {
  name: string;
  label?: string;
  // eslint-disable-next-line no-undef
  description?: string | JSX.Element | undefined;
  showAsTooltip?: boolean;
  register: any;
  errors: any;
  defaultChecked?: Array<string>;
  schema?: any;
  disabled?: boolean;
  size?: string;
  options: Array<DropdownOption>;
  isVerticalOrientation?: boolean;
}) => {
  const {
    name,
    label,
    description,
    register,
    errors,
    defaultChecked = [],
    showAsTooltip = false,
    disabled = false,
    size = 'lg',
    schema,
    options = [],
    isVerticalOrientation,
  } = props;

  return (
    <FormControl id={name}>
      <Stack textAlign="left">
        <Stack>
          <FormLabel m={0} paddingBlockStart={1} htmlFor={name}>
            <Text color={disabled ? 'disabled' : 'inherit'}>
              {Boolean(label) && label}
              {isRequired({ field: name, schema }) && <RequiredAsterisk />}
            </Text>
            {Boolean(description) && !showAsTooltip && (
              <Text color={disabled ? 'disabled' : 'inherit'} fontSize="xs">
                {description}
              </Text>
            )}
            {Boolean(description) && !disabled && showAsTooltip && <IconTooltip text={description}></IconTooltip>}
          </FormLabel>
        </Stack>
        <CheckboxGroup
          key={name}
          defaultValue={defaultChecked}
          // @ts-ignore
          borderColor={!isNil(errors[name]) && 'error'}
          size={size}
          p={2}
          pl={0}
          pb={0}
          disabled={disabled}
        >
          {isVerticalOrientation ? (
            <Stack pl={6} mt={1} spacing={1}>
              {options.map((option, index) => (
                <>
                  <Checkbox {...register(name)} key={index + '-' + name} value={option.value} size={size} p={2}>
                    <Text color="muted" fontSize="xs" m={0} key={index + '-' + option.label}>
                      {option.label}
                    </Text>
                  </Checkbox>
                </>
              ))}
            </Stack>
          ) : (
            <Grid display={'flex'} flexDirection={'row'} flexWrap={'wrap'}>
              {options.map((option, index) => (
                <>
                  <Checkbox {...register(name)} key={index + '-' + name} value={option.value} size={size} p={2}>
                    <Text color="muted" fontSize="xs" m={0}>
                      {option.label}
                    </Text>
                    <Divider orientation="vertical" />
                  </Checkbox>
                </>
              ))}
            </Grid>
          )}
        </CheckboxGroup>
        <Box pl={10}>
          <Text fontSize="sm" color={'error'} data-test-id={name + '_error'}>
            {get(errors, name, {})?.message}
          </Text>
        </Box>
      </Stack>
    </FormControl>
  );
};
export const dropdown = <TFieldValues extends FieldValues>(props: {
  name: FieldPath<TFieldValues>;
  label?: string;
  description?: string | undefined;
  control: Control<TFieldValues, any, any> | undefined;
  showAsTooltip?: boolean;
  disabled?: boolean;
  errors: any;
  options: any[];
  schema?: any;
  defaultValue?: string;
  resetField?: any;
}) => {
  const { name, label, description, control, errors, options, schema, showAsTooltip = false, disabled = false, defaultValue: _defaultValue } = props;
  return (
    <FormControl id={name} isInvalid={!!get(errors, name, {})?.message}>
      <FormLabel size="lg" htmlFor={name}>
        {label && (
          <>
            {label} {isRequired({ field: name, schema }) && <RequiredAsterisk />}
          </>
        )}
        {Boolean(description) && !showAsTooltip && (
          <Text color="muted" fontSize="xs">
            {description}
          </Text>
        )}
        {Boolean(description) && showAsTooltip && <IconTooltip text={description}></IconTooltip>}
      </FormLabel>

      <Controller
        name={name}
        control={control}
        rules={{ required: true }}
        render={({ field: { onChange, value } }) => {
          return (
            <Select
              bgColor={'white'}
              disabled={disabled}
              aria-label={label}
              value={options.find((c) => String(c.value) === String(value))?.value ?? ''}
              onChange={onChange}
              errorBorderColor={useColorModeValue('status.error', 'status.error')}
              focusBorderColor={useColorModeValue('accent', 'accent')}
            >
              {options.map((option, index) => (
                <option key={index + '-' + name} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          );
        }}
      />
      <Text fontSize="sm" color={'error'} data-test-id={name + '_error'}>
        {get(errors, name, {})?.message}
      </Text>
    </FormControl>
  );
};

export const dropdownGrouped = <TFieldValues extends FieldValues>(props: {
  name: FieldPath<TFieldValues>;
  label: string;
  description?: string | undefined;
  control: Control<TFieldValues, any, any> | undefined;
  showAsTooltip?: boolean;
  disabled?: boolean;
  errors: any;
  options: any[];
  schema?: any;
}) => {
  const { name, label, description, control, errors, options, schema, showAsTooltip = false, disabled = false } = props;

  return (
    <FormControl id={name} isInvalid={!!get(errors, name, {})?.message}>
      <FormLabel size="lg" htmlFor={name}>
        {label} {isRequired({ field: name, schema }) && <RequiredAsterisk />}
        {Boolean(description) && !showAsTooltip && (
          <Text color="muted" fontSize="xs">
            {description}
          </Text>
        )}
        {Boolean(description) && showAsTooltip && <IconTooltip text={description}></IconTooltip>}
      </FormLabel>

      <Controller
        name={name}
        control={control}
        rules={{ required: true }}
        render={({ field: { onChange, value } }) => (
          <Select
            disabled={disabled}
            aria-label={label}
            value={getArrayForGroupedDropdown(options).find((c: DropdownOption) => c.value === value)?.value ?? ''}
            onChange={onChange}
            focusBorderColor={useColorModeValue('blue.500', 'blue.200')}
          >
            {options.map((option, index) => (
              <optgroup label={option.label} key={index}>
                {get(option, 'options', []).map((subOption: DropdownOption, subIndex: number) => (
                  <option value={subOption.value} key={subIndex}>
                    {subOption.label}
                  </option>
                ))}
              </optgroup>
            ))}
          </Select>
        )}
      />
      <Text fontSize="sm" color={'error'} data-test-id={name + '_error'}>
        {get(errors, name, {})?.message}
      </Text>
    </FormControl>
  );
};

export const simpleInput = (props: {
  preventLastPassAutoFill?: boolean;
  name: string;
  label?: string;
  description?: string | undefined;
  placeholder?: string;
  register: any;
  errors: any;
  disabled?: boolean;
  type?: string;
  schema?: any;
  showAsTooltip?: boolean;
  leftAddon?: string | ReactElement;
  rightAddon?: string | ReactElement;
  trigger?: () => void;
  onFocus?: () => void;
  noAddonSpacing?: boolean;
  customHook?: (val: any) => any;
  defaultValue?: string;
  value?: string;
}) => {
  const {
    preventLastPassAutoFill,
    name,
    placeholder,
    label = false,
    description,
    register,
    disabled,
    errors,
    type,
    schema,
    showAsTooltip = false,
    noAddonSpacing = false,
    leftAddon,
    rightAddon,
    onFocus = false,
    trigger = false,
    customHook = (val: any) => val,
    defaultValue,
    value,
  } = props;
  return (
    <FormControl id={name} isInvalid={!!get(errors, name, {})?.message}>
      <FormLabel size="lg">
        {label} {label && isRequired({ field: name, schema }) && <RequiredAsterisk />}
        {Boolean(description) && !showAsTooltip && <Text fontSize="xs">{description}</Text>}
        {Boolean(description) && showAsTooltip && <IconTooltip text={description}></IconTooltip>}
      </FormLabel>
      <InputGroup>
        {leftAddon && ( //@ts-ignore
          <InputLeftAddon p={noAddonSpacing && '0'}>{leftAddon}</InputLeftAddon>
        )}
        <Input
          data-lpignore={preventLastPassAutoFill}
          type={type ?? 'text'}
          {...register(name)}
          errorBorderColor="status.error"
          disabled={disabled}
          onBlur={trigger || noop}
          onFocus={onFocus || noop}
          placeholder={placeholder}
          //@ts-ignore
          onInput={(e) => customHook(e.target.value)}
          _placeholder={{ opacity: 0.5 }}
          defaultValue={defaultValue}
          value={value}
        />
        {rightAddon && ( //@ts-ignore
          <InputRightAddon p={noAddonSpacing && '0'}>{rightAddon} </InputRightAddon>
        )}
      </InputGroup>
      <Text mt={1} pl="1" fontSize="sm" textAlign="left" color={'error'} data-test-id={name + '_error'}>
        {get(errors, name, {})?.message}
      </Text>
    </FormControl>
  );
};

export const pinInput = (props: {
  name: string;
  label?: string;
  description?: string | undefined;
  placeholder?: string;
  register: any;
  errors: any;
  disabled?: boolean;
  schema?: any;
  showAsTooltip?: boolean;
  pinAmount?: number;
  control: any;
  size?: string;
}) => {
  const {
    name,
    placeholder,
    label = false,
    description,
    register,
    disabled,
    errors,
    schema,
    showAsTooltip = false,
    control,
    size = 'lg',
    pinAmount = 4,
  } = props;

  const renderedPinInputFields = Array(pinAmount)
    .fill(null)
    .map((_noop, i) => <PinInputField _placeholder={{ opacity: 0.5 }} key={i} />);

  return (
    <Controller
      {...register(name)}
      control={control}
      isInvalid={!!get(errors, name, {})?.message}
      render={({ field }) => (
        <Stack spacing={2} alignItems={'center'}>
          <FormLabel size="lg">
            {label} {label && isRequired({ field: name, schema }) && <RequiredAsterisk />}
            {Boolean(description) && !showAsTooltip && <Text fontSize="xs">{description}</Text>}
            {Boolean(description) && showAsTooltip && <IconTooltip text={description}></IconTooltip>}
          </FormLabel>
          <HStack>
            <PinInput size={size} id={name} placeholder={placeholder} isDisabled={disabled} {...field} errorBorderColor="status.error">
              {renderedPinInputFields}
            </PinInput>
          </HStack>

          <Text mt={1} pl="1" fontSize="sm" textAlign="left" color={'error'} data-test-id={name + '_error'}>
            {get(errors, name, {})?.message}
          </Text>
        </Stack>
      )}
    />
  );
};

export const numberInput = (props: {
  name: string;
  label: string;
  description?: string | undefined;
  placeholder?: string;
  register: any;
  errors: any;
  disabled?: boolean;
  min?: number;
  max?: number;
  schema?: any;
  showAsTooltip?: boolean;
}) => {
  const { name, label, description, register, placeholder, disabled, errors, min, max, schema, showAsTooltip = false } = props;
  return (
    <FormControl id={name} isInvalid={!!get(errors, name, {})?.message}>
      <FormLabel size="lg">
        {label} {isRequired({ field: name, schema }) && <RequiredAsterisk />}
        {Boolean(description) && !showAsTooltip && (
          <Text color="muted" fontSize="xs">
            {description}
          </Text>
        )}
        {Boolean(description) && showAsTooltip && <IconTooltip text={description}></IconTooltip>}
      </FormLabel>
      <Input
        type={'number'}
        min={min}
        max={max}
        errorBorderColor="status.error"
        placeholder={placeholder}
        _placeholder={{ opacity: 0.5 }}
        {...register(name)}
        disabled={disabled}
      />
      <Text mt={1} pl="1" fontSize="sm" color={'error'} data-test-id={name + '_error'}>
        {get(errors, name, {})?.message}
      </Text>
    </FormControl>
  );
};

export const textareaInput = (props: {
  name: string;
  label?: ReactNode;
  description?: string | undefined;
  placeholder?: string | undefined;
  register: any;
  errors: any;
  disabled?: boolean;
  schema?: any;
  showAsTooltip?: boolean;
  defaultValue?: string;
  trigger?: () => void;
  onFocus?: () => void;
  required?: boolean;
}) => {
  const {
    trigger,
    onFocus,
    name,
    label,
    description,
    register,
    disabled,
    errors,
    placeholder,
    schema,
    showAsTooltip = false,
    defaultValue = '',
    required,
  } = props;
  return (
    <FormControl id={name} isInvalid={!!get(errors, name, {})?.message}>
      <FormLabel size="lg">
        {label} {isRequired({ field: name, schema }) && <RequiredAsterisk />}
        {Boolean(description) && !showAsTooltip && (
          <Text color="muted" fontSize="xs">
            {description}
          </Text>
        )}
        {Boolean(description) && showAsTooltip && <IconTooltip text={description}></IconTooltip>}
      </FormLabel>
      <Textarea
        data-test-id={name}
        minH="10rem"
        {...register(name)}
        disabled={disabled}
        placeholder={placeholder}
        _placeholder={{ opacity: 0.5 }}
        errorBorderColor="status.error"
        onBlur={trigger || noop}
        onFocus={onFocus || noop}
        defaultValue={defaultValue}
        required={required}
      />
      <Text mt={1} pl="1" fontSize="sm" color={'error'} data-test-id={name + '_error'}>
        {get(errors, name, {})?.message}
      </Text>
    </FormControl>
  );
};
export const dateInput = (props: {
  name: string;
  label: ReactNode;
  description?: string | undefined;
  control: any;
  errors: any;
  disabled?: boolean;
  minDate?: Date;
  maxDate?: Date;
  schema?: any;
  showAsTooltip?: boolean;
  noAsterisk?: boolean;
}) => {
  const { name, label, description, control, disabled, errors, minDate, maxDate, schema, showAsTooltip, noAsterisk = false } = props;
  return (
    <FormControl id={name} isInvalid={!!get(errors, name, {})?.message}>
      <FormLabel size="lg">
        {label} {isRequired({ field: name, schema }) && !noAsterisk && <RequiredAsterisk />}
        {Boolean(description) && !showAsTooltip && (
          <Text color="muted" fontSize="xs">
            {description}
          </Text>
        )}
        {Boolean(description) && showAsTooltip && <IconTooltip text={description}></IconTooltip>}
      </FormLabel>
      <Controller
        name={name}
        control={control}
        rules={{ required: true }}
        render={({ field }) => (
          <SingleDatepicker
            //@ts-ignore
            date={field.value ? new Date(field.value) : undefined}
            name={'date_' + name}
            disabled={disabled}
            onDateChange={field.onChange}
            minDate={minDate}
            maxDate={maxDate}
            configs={{ dateFormat: 'dd.MM.yyyy' }}
          />
        )}
      />
      <Text mt={1} pl="1" fontSize="sm" color={'error'} data-test-id={name + '_error'}>
        {get(errors, name, {})?.message}
      </Text>
    </FormControl>
  );
};

export const dropzone = <TFieldValues extends FieldValues>(props: {
  name: FieldPath<TFieldValues>;
  label?: ReactNode;
  description?: ReactNode;
  control: Control<TFieldValues, any, any> | undefined;
  errors: any;
  isLoading?: boolean;
  schema?: any;
  disabled?: boolean;
  maxFiles?: number;
  onChange?: (arg0: any) => void;
}) => {
  const { name, label, description, control, errors, isLoading = false, schema, disabled = false, maxFiles = 1, onChange } = props;
  if (isLoading) {
    return <Skeleton height="200px" />;
  }
  return (
    <FormControl id={name} isInvalid={!!get(errors, name, {})?.message}>
      <FormLabel size="lg" htmlFor={name}>
        {label} {isRequired({ field: name, schema }) && <RequiredAsterisk />}
      </FormLabel>
      <Text fontSize="sm">{description}</Text>

      <Controller
        name={name}
        data-test-id={name}
        control={control}
        rules={{ required: true }}
        render={({ field }) => (
          <DropzoneInput
            disabled={disabled}
            identifier={`${name}_zone ${name.split('common:.').pop()}`}
            onChange={(arg) => {
              onChange?.(arg);
              field.onChange(arg);
            }}
            maxFiles={maxFiles}
          />
        )}
      />
      <Text mt={1} pl="1" fontSize="sm" color={'error'} data-test-id={name + '_error'}>
        {get(errors, name, {})?.message}
      </Text>
    </FormControl>
  );
};
